import React from "react";
import { cn } from "../../lib/utils";
import { Switch } from "../../internal/primitives/switch";
import { Label } from "../../internal/primitives/label";
import type {
  UserSettingsAppearancePanelProps,
  UserSettingsThemeOption,
  UserSettingsSwatchOption,
  UserSettingsChoiceOption,
} from "../../contracts/user-settings";
import { Check, CheckCircle2 } from "lucide-react";
import {
  VerticalStack,
  HorizontalStack,
  SectionEyebrow,
  DescriptionText,
  RegistryStatus,
  SectionSeparator
} from "../blocks";

export function UserSettingsAppearancePanel({
  title = "Appearance",
  description = "Customize how the workspace looks and feels.",
  showHeader = false,
  selectedTheme,
  activeTheme,
  themeOptions,
  onThemeChange,
  selectedEffect,
  effectOptions,
  onEffectChange,
  selectedLayout,
  layoutOptions,
  onLayoutChange,
  selectedToastPosition,
  toastPositionOptions,
  onToastPositionChange,
  selectedMapTheme,
  mapThemeOptions,
  onMapThemeChange,
  mapThemeOverrideEnabled,
  onMapThemeOverrideEnabledChange,
  mapThemeDescription,
  disabled,
  className,
}: UserSettingsAppearancePanelProps) {
  const renderThemeOption = (option: UserSettingsThemeOption) => {
    const isSelected = selectedTheme === option.value;
    const isActive = activeTheme === option.value;

    return (
      <button
        key={option.value}
        onClick={() => !disabled && !option.disabled && onThemeChange(option.value)}
        disabled={disabled || option.disabled}
        className={cn(
          "group relative flex flex-col items-start gap-3 p-4 rounded-lg border transition-all text-left w-full",
          isSelected
            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
            : "border-border/60 hover:border-border hover:bg-muted/30",
          disabled || option.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        <HorizontalStack gap={3} className="w-full">
          <div
            className="w-10 h-10 rounded border border-border/50 shrink-0 shadow-sm"
            style={{ background: option.preview }}
          />
          <div className="min-w-0 flex-1">
            <HorizontalStack gap={2}>
              <span className="text-xs font-bold text-foreground truncate">
                {option.label}
              </span>
              {isActive && (
                <RegistryStatus label="Active" variant="default" className="text-[10px]" />
              )}
            </HorizontalStack>
            {option.description && (
              <span className="text-[10px] text-muted-foreground line-clamp-1">
                {option.description}
              </span>
            )}
          </div>
          {isSelected && (
            <div className="h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
              <Check className="h-3 w-3" />
            </div>
          )}
        </HorizontalStack>
        <div className="h-1 w-full rounded-full bg-border/20 overflow-hidden mt-1">
           <div className="h-full w-1/3" style={{ backgroundColor: option.accent }} />
        </div>
      </button>
    );
  };

  const renderSwatchOption = (option: UserSettingsSwatchOption) => {
    const isSelected = selectedEffect === option.value;
    return (
      <button
        key={option.value}
        onClick={() => !disabled && !option.disabled && onEffectChange(option.value)}
        disabled={disabled || option.disabled}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border transition-all text-left w-full",
          isSelected
            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
            : "border-border/60 hover:border-border hover:bg-muted/30",
          disabled || option.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        <div
          className="w-6 h-6 rounded-full border border-border/50 shrink-0 shadow-inner"
          style={option.swatchStyle}
        />
        <div className="min-w-0">
          <div className="text-[11px] font-bold text-foreground leading-none">{option.label}</div>
          {isSelected && <div className="text-[9px] text-primary font-bold uppercase mt-0.5">Selected</div>}
        </div>
      </button>
    );
  };

  const renderChoiceOption = (
    option: UserSettingsChoiceOption,
    current: string,
    onSelect: (val: string) => void
  ) => {
    const isSelected = current === option.value;
    return (
      <button
        key={option.value}
        onClick={() => !disabled && !option.disabled && onSelect(option.value)}
        disabled={disabled || option.disabled}
        className={cn(
          "flex flex-col items-start gap-1 p-3 rounded-lg border transition-all text-left w-full",
          isSelected
            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
            : "border-border/60 hover:border-border hover:bg-muted/30",
          disabled || option.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        <div className="flex w-full items-center gap-2">
          {option.icon && (
            <span className={cn(
              "shrink-0 transition-colors",
              isSelected ? "text-primary" : "text-muted-foreground"
            )}>
              {option.icon}
            </span>
          )}
          <span className="text-xs font-bold text-foreground flex-1 truncate">{option.label}</span>
          {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />}
        </div>
        {option.description && (
          <span className="text-[10px] text-muted-foreground leading-tight pl-0">
            {option.description}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      <VerticalStack gap={8}>
        {/* Optional standalone header — suppressed when embedded inside UserSettingsPage */}
        {showHeader && (
          <>
            <div className="space-y-1">
              <h2 className="text-xl font-bold tracking-tight text-foreground font-heading">
                {title}
              </h2>
              <DescriptionText>{description}</DescriptionText>
            </div>
            <SectionSeparator />
          </>
        )}

        {/* Workspace Theme */}
        <VerticalStack gap={3}>
          <SectionEyebrow>Workspace Theme</SectionEyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {themeOptions.map(renderThemeOption)}
          </div>
        </VerticalStack>

        <SectionSeparator />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Visual Effects */}
          <VerticalStack gap={3}>
            <SectionEyebrow>Visual Effects</SectionEyebrow>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {effectOptions.map(renderSwatchOption)}
            </div>
          </VerticalStack>

          {/* Navigation Layout */}
          <VerticalStack gap={3}>
            <SectionEyebrow>Navigation Layout</SectionEyebrow>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {layoutOptions.map(opt => renderChoiceOption(opt, selectedLayout, onLayoutChange))}
            </div>
          </VerticalStack>
        </div>

        <SectionSeparator />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Toast Notifications */}
          <VerticalStack gap={3}>
            <SectionEyebrow>Notifications</SectionEyebrow>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {toastPositionOptions.map(opt =>
                renderChoiceOption(opt, selectedToastPosition, onToastPositionChange)
              )}
            </div>
          </VerticalStack>

          {/* Map Basemap */}
          <VerticalStack gap={3}>
            <HorizontalStack className="justify-between">
              <SectionEyebrow>Basemap</SectionEyebrow>
              <HorizontalStack gap={2}>
                <Switch
                  id="map-theme-override"
                  checked={mapThemeOverrideEnabled}
                  onCheckedChange={onMapThemeOverrideEnabledChange}
                  disabled={disabled}
                />
                <Label
                  htmlFor="map-theme-override"
                  className="text-[10px] font-black uppercase text-muted-foreground cursor-pointer tracking-wider"
                >
                  Override
                </Label>
              </HorizontalStack>
            </HorizontalStack>

            {mapThemeOverrideEnabled ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {mapThemeOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => !disabled && onMapThemeChange(opt.value)}
                    disabled={disabled}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                      selectedMapTheme === opt.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                        : "border-border/60 hover:border-border hover:bg-muted/30",
                      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    )}
                  >
                    <div
                      className="w-8 h-8 rounded border border-border/50 shrink-0 shadow-sm"
                      style={{ background: opt.preview }}
                    />
                    <span className="text-[11px] font-bold text-foreground flex-1 truncate">
                      {opt.label}
                    </span>
                    {selectedMapTheme === opt.value && (
                      <Check className="ml-auto h-3 w-3 text-primary shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground italic leading-snug pt-1">
                {mapThemeDescription ||
                  "Map basemap is currently synced to the workspace theme."}
              </p>
            )}
          </VerticalStack>
        </div>
      </VerticalStack>
    </div>
  );
}
